<template>
  <div class="invite-page">
    <div class="invite-card">
      <!-- Logo / brand -->
      <div class="invite-brand">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect width="32" height="32" rx="8" fill="var(--accent,#2563eb)"/>
          <path d="M8 24L16 8l8 16" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.5 19h11" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span class="invite-brand-name">{{ systemName }}</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="invite-state">
        <div class="spinner" />
        <p>Verificando convite…</p>
      </div>

      <!-- Error -->
      <div v-else-if="loadError" class="invite-state invite-state--error">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="#ef4444" stroke-width="2"/><path d="M20 12v10M20 26v2" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/></svg>
        <h2>Convite inválido</h2>
        <p>{{ loadError }}</p>
      </div>

      <!-- Success -->
      <div v-else-if="accepted" class="invite-state invite-state--success">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="22" stroke="#22c55e" stroke-width="2"/><path d="M14 24l7 7 13-14" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <h2>Conta criada!</h2>
        <p>Sua conta foi criada com sucesso. Você já pode fazer login.</p>
        <button class="btn-primary" @click="goLogin">Ir para o login</button>
      </div>

      <!-- Form -->
      <template v-else-if="invite">
        <div class="invite-header">
          <h1>Você foi convidado!</h1>
          <p class="invite-sub">Crie sua conta em <strong>{{ invite.systemName }}</strong> como <span class="role-chip">{{ roleLabel(invite.role) }}</span></p>
        </div>

        <div class="invite-email-row">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.3"/><path d="M1 5l7 5 7-5" stroke="currentColor" stroke-width="1.3"/></svg>
          {{ invite.email }}
        </div>

        <div class="invite-form">
          <div class="field">
            <label>Nome completo</label>
            <input v-model="form.name" type="text" placeholder="Seu nome" autocomplete="name" />
          </div>
          <div class="field">
            <label>Username</label>
            <input v-model="form.username" type="text" placeholder="Ex: joaosilva" autocomplete="username" />
          </div>
          <div class="field">
            <label>Data de nascimento</label>
            <input v-model="form.birthDate" type="date" :min="MIN_BIRTH_DATE" :max="maxBirthDate()" autocomplete="bday" required />
          </div>
          <div class="field">
            <label>Senha</label>
            <div class="pw-wrap">
              <input v-model="form.password" :type="showPw ? 'text' : 'password'" placeholder="Mín. 8 caracteres, 1 número" autocomplete="new-password" />
              <button type="button" class="pw-toggle" @click="showPw = !showPw">
                <svg v-if="!showPw" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.3"/></svg>
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 1l14 14M6.5 6.7A2 2 0 0 0 9.3 9.5M3.7 3.9C2.2 5 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.8-1M6 2.2C6.6 2.1 7.3 2 8 2c4.5 0 7 5 7 5s-.7 1.5-2 2.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <p v-if="submitError" class="field-error">{{ submitError }}</p>
          <button class="btn-primary" :disabled="submitting" @click="submit">
            <span v-if="submitting" class="spinner-sm" />
            {{ submitting ? 'Criando conta…' : 'Criar conta' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { apiStore } from '@/store/api'
import { birthDateError, maxBirthDate, MIN_BIRTH_DATE } from '@/utils/birthDate'

export default defineComponent({
  name: 'AcceptInviteView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const token = (route.query.token as string) || ''

    const loading = ref(true)
    const loadError = ref('')
    const accepted = ref(false)
    const submitting = ref(false)
    const submitError = ref('')
    const showPw = ref(false)
    const invite = ref<any>(null)
    const systemName = ref('Obra Tycoon')

    const form = ref({ name: '', username: '', password: '', birthDate: '' })

    const roleLabel = (role: string) => {
      if (role === 'admin-master') return 'Master'
      if (role === 'admin-obra') return 'Administrador'
      return 'Usuário'
    }

    onMounted(async () => {
      if (!token) { loadError.value = 'Link inválido.'; loading.value = false; return }
      try {
        const res = await fetch(`${apiStore.base}/invite-info/${token}`)
        const data = await res.json()
        if (!res.ok) { loadError.value = data.error || 'Convite inválido.'; loading.value = false; return }
        invite.value = data.invite
        systemName.value = data.invite.systemName || 'Obra Tycoon'
      } catch {
        loadError.value = 'Erro ao verificar convite. Tente novamente.'
      }
      loading.value = false
    })

    const submit = async () => {
      submitError.value = ''
      if (!form.value.name.trim() || !form.value.username.trim() || !form.value.password) {
        submitError.value = 'Preencha todos os campos.'; return
      }
      if (form.value.password.length < 8 || !/\d/.test(form.value.password)) {
        submitError.value = 'Senha deve ter pelo menos 8 caracteres e 1 número.'; return
      }
      const birthErr = birthDateError(form.value.birthDate)
      if (birthErr) { submitError.value = birthErr; return }
      submitting.value = true
      try {
        const res = await fetch(`${apiStore.base}/accept-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, ...form.value })
        })
        const data = await res.json()
        if (!res.ok) { submitError.value = data.error || 'Erro ao criar conta.'; submitting.value = false; return }
        accepted.value = true
      } catch {
        submitError.value = 'Erro de conexão. Tente novamente.'
      }
      submitting.value = false
    }

    const goLogin = () => {
      if (invite.value?.systemUsername) {
        router.push(`/login/${invite.value.systemUsername}`)
      } else {
        router.push('/')
      }
    }

    return { MIN_BIRTH_DATE, maxBirthDate, loading, loadError, accepted, submitting, submitError, showPw, invite, systemName, form, roleLabel, submit, goLogin }
  }
})
</script>

<style scoped lang="scss">
.invite-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg, #f5f2ee);
  padding: 24px;
}

.invite-card {
  background: var(--card, #fff);
  border-radius: 16px;
  box-shadow: 0 4px 32px rgba(0,0,0,.1);
  padding: 40px 36px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.invite-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}
.invite-brand-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink, #1f1e1a);
}

.invite-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  text-align: center;
  h2 { margin: 0; font-size: 20px; color: var(--ink, #1f1e1a); }
  p { margin: 0; color: var(--ink-muted, #888); font-size: 14px; }
  &--error h2 { color: #ef4444; }
  &--success h2 { color: #22c55e; }
}

.invite-header {
  h1 { margin: 0 0 6px; font-size: 22px; color: var(--ink, #1f1e1a); }
}
.invite-sub { margin: 0; font-size: 14px; color: var(--ink-muted, #888); }

.role-chip {
  display: inline-block;
  background: var(--accent, #2563eb);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
}

.invite-email-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--ink-muted, #888);
  background: var(--surface, rgba(0,0,0,.04));
  padding: 8px 12px;
  border-radius: 8px;
  svg { flex-shrink: 0; }
}

.invite-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-muted, #888);
    text-transform: uppercase;
    letter-spacing: .5px;
  }
  input {
    padding: 9px 12px;
    border: 1.5px solid var(--border, rgba(0,0,0,.12));
    border-radius: 8px;
    font-size: 14px;
    color: var(--ink, #1f1e1a);
    background: var(--bg, #fff);
    outline: none;
    transition: border-color .15s;
    &:focus { border-color: var(--accent, #2563eb); }
  }
}

.pw-wrap {
  position: relative;
  input { width: 100%; padding-right: 40px; box-sizing: border-box; }
}
.pw-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-muted, #888);
  padding: 4px;
  &:hover { color: var(--ink, #1f1e1a); }
}

.field-error {
  margin: 0;
  font-size: 12px;
  color: #ef4444;
}

.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: var(--accent, #2563eb);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity .15s;
  &:hover:not(:disabled) { opacity: .88; }
  &:disabled { opacity: .55; cursor: not-allowed; }
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border, rgba(0,0,0,.12));
  border-top-color: var(--accent, #2563eb);
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
